import { useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import { Grid, Stack } from '@mui/material'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import Page from '../pages/Page'
import FormattedDate from '../table/FormattedDate'
import FetchContext from '../../context/fetch/FetchContext'
import { DASH, EMAIL_KIND_LABELS, EMAIL_OUTCOME_LABELS, labelOf, NO_FIRM_LABEL } from './emailLabels'

export const emailRecordQueryKey = (emailId) => ['emails', emailId]

function Property({ label, children }) {
    return (
        <Stack spacing={0.5}>
            <MDTypography variant="caption" color="text">{label}</MDTypography>
            <MDTypography variant="button" fontWeight="medium">{children}</MDTypography>
        </Stack>
    )
}

export default function EmailRecordPage() {
    const { emailId } = useParams()
    const fetcher = useContext(FetchContext)

    const { data: record, isLoading } = useQuery({
        queryKey: emailRecordQueryKey(emailId),
        queryFn: () => fetcher.fetchEmail(emailId),
    })

    const body = record?.body == null
        ? null
        : DOMPurify.sanitize(record.body)

    return (
        <Page className="email-record" title="הודעה" busy={isLoading}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                    <Property label="סוג">{labelOf(EMAIL_KIND_LABELS, record?.kind)}</Property>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Property label="מצב">{labelOf(EMAIL_OUTCOME_LABELS, record?.outcome)}</Property>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Property label="משרד">{record?.firm?.name ?? NO_FIRM_LABEL}</Property>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Property label="נשלח בידי">{record?.user?.username ?? DASH}</Property>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Property label="מאת">{record?.fromAddress ?? DASH}</Property>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Property label="אל">{record?.toAddress ?? DASH}</Property>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Property label="נושא">{record?.subject ?? DASH}</Property>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Property label="מועד">
                        {record?.sentAt == null
                            ? DASH
                            : <FormattedDate value={record.sentAt} />}
                    </Property>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Property label="מזהה אצל שירות הדואר">{record?.messageId ?? DASH}</Property>
                </Grid>
                {record?.failureDetail != null && (
                    <Grid item xs={12}>
                        <Property label="פרטי הכשל">
                            <MDBox
                                data-testid="email-record-failure"
                                component="pre"
                                sx={{ whiteSpace: 'pre-wrap', direction: 'ltr' }}
                            >
                                {record.failureDetail}
                            </MDBox>
                        </Property>
                    </Grid>
                )}
            </Grid>

            <MDTypography variant="h6" sx={{ mb: 1 }}>תוכן ההודעה</MDTypography>
            <MDBox
                data-testid="email-record-body"
                sx={{
                    padding: 2,
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 1,
                    backgroundColor: 'white',
                }}
                dangerouslySetInnerHTML={{ __html: body ?? '' }}
            />
        </Page>
    )
}
